import { useCallback, useEffect, useRef, useState } from "react";
import type { Page } from "./api";
import { errorText } from "./i18n";
import { useRefreshHandler } from "./Refresh";

/**
 * A server list page by page (PLAN.md 4.20 step 6): the first page when `key` changes, the next one
 * on `loadMore`. `reload` (Refresh, after a change) asks for the first page again. `fetchPage` may be
 * a new function on every render; `key` says when the list itself changed.
 */
export function usePagedList<T>(key: string, fetchPage: (cursor?: string) => Promise<Page<T>>, enabled = true) {
  const [items, setItems] = useState<T[] | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;
  const generation = useRef(0);

  const reload = useCallback(async () => {
    const mine = ++generation.current;
    setError(null);
    if (!enabled) { setItems(null); setNext(null); return; }
    try {
      const page = await fetchRef.current();
      if (mine !== generation.current) return;
      setItems(page.items);
      setNext(page.next_cursor);
    } catch (err) {
      if (mine === generation.current) setError(errorText(err));
    }
  }, [key, enabled]);

  const loadMore = useCallback(async () => {
    if (!next || loadingMore) return;
    const mine = generation.current;
    setLoadingMore(true);
    try {
      const page = await fetchRef.current(next);
      if (mine !== generation.current) return;
      setItems((current) => [...(current ?? []), ...page.items]);
      setNext(page.next_cursor);
    } catch (err) {
      if (mine === generation.current) setError(errorText(err));
    } finally { setLoadingMore(false); }
  }, [next, loadingMore]);

  useEffect(() => { setItems(null); setNext(null); void reload(); }, [reload]);
  useRefreshHandler(reload);
  return { items, hasMore: next !== null, loadingMore, error, reload, loadMore };
}

/** A long list the page already holds (follows, blocks, hides: at most 500 to 1000), shown 50 at a time. */
export function useShowMore<T>(list: T[], step = 50) {
  const [count, setCount] = useState(step);
  return { shown: list.slice(0, count), hasMore: list.length > count, showMore: () => setCount((n) => n + step) };
}
